import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Department Types & API
// ============================================================================

export interface Department {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    memberCount: number;
    isActive: boolean;
    createdAt: string;
}

export interface DepartmentRequest {
    name: string;
    description?: string;
}

export const departmentApi = {
    getAll: () => apiClient.get<ApiResponse<Department[]>>("/b2b/departments"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<Department>>(`/b2b/departments/${id}`),

    create: (data: DepartmentRequest) =>
        apiClient.post<ApiResponse<Department>>("/b2b/departments", data),

    update: (id: number, data: DepartmentRequest) =>
        apiClient.put<ApiResponse<Department>>(`/b2b/departments/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/departments/${id}`),
};

// ============================================================================
// Special Group Types & API
// ============================================================================

export interface SpecialGroup {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    memberCount: number;
    isActive: boolean;
    createdAt: string;
}

export interface SpecialGroupRequest {
    name: string;
    description?: string;
}

export interface SpecialGroupMember {
    userId: number;
    fullName: string;
    email: string;
    departmentName: string | null;
    joinedAt: string | null;
}

export interface SpecialGroupMemberRequest {
    userIds: number[];
}

export const specialGroupApi = {
    getAll: () => apiClient.get<ApiResponse<SpecialGroup[]>>("/b2b/special-groups"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<SpecialGroup>>(`/b2b/special-groups/${id}`),

    create: (data: SpecialGroupRequest) =>
        apiClient.post<ApiResponse<SpecialGroup>>("/b2b/special-groups", data),

    update: (id: number, data: SpecialGroupRequest) =>
        apiClient.put<ApiResponse<SpecialGroup>>(`/b2b/special-groups/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/special-groups/${id}`),

    // 멤버 관리
    getMembers: (groupId: number) =>
        apiClient.get<ApiResponse<SpecialGroupMember[]>>(`/b2b/special-groups/${groupId}/members`),

    addMembers: (groupId: number, userIds: number[]) =>
        apiClient.post<ApiResponse<SpecialGroupMember[]>>(`/b2b/special-groups/${groupId}/members`, { userIds }),

    removeMembers: (groupId: number, userIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/special-groups/${groupId}/members`, { data: { userIds } }),
};

// ============================================================================
// Member Types & API
// ============================================================================

export interface Member {
    userId: number;
    email: string;
    fullName: string;
    phoneNumber: string | null;
    countryCode?: string;
    isPhoneVerified?: boolean;
    departmentId: number | null;
    departmentName: string | null;
    employeeId: string | null;
    position: string | null;
    role: string;
    status: string;
    joinedAt: string | null;
    createdAt: string;
    classNames?: string[] | null;  // 소속 클래스 목록
    linkedProductName?: string | null;  // 연결된 상품명 (첫 번째 클래스 기준)
    classProductMap?: Record<string, string | null> | null;  // 클래스명 → 상품명 매핑
    specialGroupNames?: string[] | null;  // 소속 학습그룹 목록
    accountStatus?: string | null;  // 'PENDING' | 'INVITED' | 'ACTIVE'
}

export interface MemberBulkResult {
    success: Member[];
    failed: MemberBulkError[];
}

export interface MemberBulkError {
    email: string;
    fullName: string;
    reason: string;
}

export interface MemberRequest {
    email: string;
    fullName: string;
    password?: string;
    departmentId?: number;
    employeeId?: string;
    position?: string;
    role?: string;
    phoneNumber?: string;
}

export interface MemberUpdateRequest {
    departmentId?: number;
    employeeId?: string;
    position?: string;
    role?: string;
}

// Send Credentials
export interface SendCredentialResult {
    userId: number;
    email: string;
    status: string;  // 'SENT' | 'FAILED'
    error?: string;
}

export interface SendCredentialsResponse {
    total: number;
    sent: number;
    failed: number;
    results: SendCredentialResult[];
}

export const memberApi = {
    getAll: () => apiClient.get<ApiResponse<Member[]>>("/b2b/members"),

    getByDepartment: (departmentId: number) =>
        apiClient.get<ApiResponse<Member[]>>(
            `/b2b/members/department/${departmentId}`
        ),

    getById: (userId: number) =>
        apiClient.get<ApiResponse<Member>>(`/b2b/members/${userId}`),

    create: (data: MemberRequest) =>
        apiClient.post<ApiResponse<Member>>("/b2b/members", data),

    createBulk: (members: MemberRequest[]) =>
        apiClient.post<ApiResponse<MemberBulkResult>>("/b2b/members/bulk", { members }),

    update: (userId: number, data: MemberUpdateRequest) =>
        apiClient.put<ApiResponse<Member>>(`/b2b/members/${userId}`, data),

    delete: (userId: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/members/${userId}`),

    sendCredentials: (userIds: number[]) =>
        apiClient.post<ApiResponse<SendCredentialsResponse>>('/b2b/members/send-credentials', { userIds }),

    resetPhoneVerification: (userId: number) =>
        apiClient.post(`/b2b/members/${userId}/reset-phone-verification`),

    resetPassword: (userId: number, newPassword?: string) =>
        apiClient.post<ApiResponse<{ temporaryPassword: string }>>(`/b2b/members/${userId}/reset-password`, { newPassword }),
};

// ============================================================================
// Learner Types & API (Class-based)
// ============================================================================

export interface ProgressUpdateRequest {
    videoProgressPercent?: number;
    quizPassed?: boolean;
    essaySubmitted?: boolean;
}

export const learnerApi = {
    // 대시보드용 프로필 조회
    getMyProfile: () =>
        apiClient.get<ApiResponse<Member>>("/b2b/my/profile"),

    getMyClassCourses: () =>
        apiClient.get<ApiResponse<LearnerClassCourse[]>>("/b2b/my/class-courses"),

    getClassCourse: (classProductId: number) =>
        apiClient.get<ApiResponse<LearnerClassCourse>>(`/b2b/my/class-courses/${classProductId}`),

    updateClassProgress: (classProductId: number, data: ProgressUpdateRequest) =>
        apiClient.put<ApiResponse<LearnerClassCourse>>(
            `/b2b/my/class-courses/${classProductId}/progress`,
            data
        ),

    // MyCourses 강의실 페이지 전용 API
    getMyCourses: () =>
        apiClient.get<ApiResponse<MyCourseResponse[]>>("/b2b/my/courses"),
};

// ============================================================================
// MyCourses 강의실 페이지 전용 Types
// ============================================================================

/**
 * 교육 구성 정보
 */
export interface CourseComposition {
    videoCount: number;  // 영상 개수 (= 레슨 개수)
    quizCount: number;   // 문제 개수 (= 레슨 개수 * 3)
}

/**
 * 코스 진도율
 */
export interface CourseProgress {
    completedLessons: number;
    totalLessons: number;
    progressPercent: number;
}

/**
 * 모듈 진행 정보
 */
export interface ModuleProgressInfo {
    id: number;
    sequenceOrder: number;
    title: string;
    progressPercent: number;
    completedLessons: number;
    totalLessons: number;
}

/**
 * 강의실 페이지용 코스 응답
 */
/**
 * 현재 진행 중인 레슨 정보
 */
export interface CurrentLessonInfo {
    lessonId: number;
    lessonTitle: string;
    thumbnailUrl: string | null;
    moduleTitle: string | null;
    moduleId?: number;
    isLastLessonInModule?: boolean;
    hasStarted?: boolean;
}

/**
 * 대기 중인 모듈 마무리 퀴즈 정보
 */
export interface PendingModuleQuizInfo {
    moduleId: number;
    moduleTitle: string;
    isEnabled: boolean;
    isCompleted: boolean;
}

export interface MyCourseResponse {
    // 식별자
    classProductId: number;
    classId: number;
    className: string;
    classDescription: string | null;
    licenseId: number;
    productId: number;
    contentId: number;

    // 커리큘럼 정보
    curriculumType: string | null;           // "공통" | "사원급" | "팀장급" | "임원급" (null이면 UI에서 더미 표시)
    curriculumTitle: string;
    curriculumBareTitle: string | null;      // curriculum의 bareTitle
    thumbnailUrl: string | null;
    curriculumStatus: string;                // "planned" | "in_progress" | "completed"
    learningObjectives: string | null;       // 교육 목표
    majorContents: string | null;            // 주요 내용
    concepts: string | null;                 // Key Concepts
    estimatedDurationMinutes: number | null; // 교육 시간

    // 코스 정보 (curriculum 하위 첫 번째 course)
    courseTitle: string | null;              // 연결된 course의 title
    courseBareTitle: string | null;          // 연결된 course의 bareTitle

    // 교육 구성
    composition: CourseComposition;

    // 진도율
    progress: CourseProgress;

    // 모듈 목록
    modules: ModuleProgressInfo[];

    // 순서 및 잠금
    sequenceOrder: number;               // 코스 순서 (1부터 시작)
    isLocked: boolean;                   // 이전 코스 미완료로 잠금 상태

    // 기존 필드
    startDate: string | null;
    deadline: string | null;
    isCompleted: boolean;
    completedAt: string | null;
    startedAt: string | null;
    lastAccessedAt: string | null;

    // 현재 진행 중인 레슨 정보 (대시보드 Quick Access용)
    currentLesson: CurrentLessonInfo | null;

    // 대기 중인 모듈 마무리 퀴즈 정보
    pendingModuleEndQuiz?: PendingModuleQuizInfo | null;
}

// Course information within a bundle content (curriculum's first course)
export interface BundleContentCourse {
    id: number;
    title: string;
    bareTitle?: string | null;
    sequenceOrder: number;
    moduleCount?: number;
    lessonCount?: number;
    thumbnailUrl?: string | null;
}

// Bundle content item for bundle products
export interface BundleContentItem {
    contentId: number;
    contentTitle: string | null;
    contentType: string;
    sequenceOrder: number;
    thumbnailUrl?: string | null;
    moduleCount?: number;
    // NEW: Course information (courses under this curriculum)
    courses?: BundleContentCourse[] | null;
}

// NEW: Progress summary with module/lesson counts
export interface ContentProgressSummary {
    totalModules: number;
    completedModules: number;
    totalLessons: number;
    completedLessons: number;
    overallProgressPercent: number;
}

// NEW: Next learning action
export interface NextLearningAction {
    actionType: string;      // "start_lesson", "continue_video", "take_quiz", "retake_quiz"
    contentId: number;
    contentTitle: string;
    moduleTitle?: string | null;
    componentType?: string | null;   // "video", "pre-quiz", "post-quiz"
    lastPositionSec?: number | null;
}

// NEW: Recent activity
export interface RecentActivity {
    lessonId?: number | null;
    lessonTitle?: string | null;
    moduleId?: number | null;
    moduleTitle?: string | null;
    activityType: string;    // "video_started", "video_completed", "quiz_attempted", "quiz_passed"
    timestamp: string;
    score?: number | null;
    passed?: boolean | null;
}

// Class-based learner course (includes classId, licenseId, productId for Configuration API)
export interface LearnerClassCourse {
    classProductId: number;
    classId: number;
    className: string;
    licenseId: number;
    productId: number;
    productName: string | null;
    contentId: number | null;
    contentType: string;
    contentTitle: string | null;
    contentDescription: string | null;
    startDate: string | null;
    deadline: string | null;
    videoProgressPercent: number;
    quizPassed: boolean;
    essaySubmitted: boolean;
    isCompleted: boolean;
    completedAt: string | null;
    startedAt: string | null;
    lastAccessedAt: string | null;
    // Bundle product support
    isBundle: boolean;
    bundleContents?: BundleContentItem[];
    courses?: {
        id: number;
        title: string;
        bareTitle?: string | null;
        sequenceOrder: number;
        moduleCount: number;
        lessonCount: number;
        thumbnailUrl?: string | null;
    }[];
    // NEW: Progress summary with module/lesson counts
    progressSummary?: ContentProgressSummary | null;
    // NEW: Next learning action
    nextAction?: NextLearningAction | null;
    // NEW: Recent activity
    recentActivity?: RecentActivity | null;
}

// ============================================================================
// Report Types & API
// ============================================================================

export interface MemberProgress {
    userId: number;
    email: string;
    fullName: string;
    departmentName: string | null;
    totalAssignments: number;
    completedAssignments: number;
    inProgressAssignments: number;
    averageProgress: number;
}

export interface ProgressReport {
    totalMembers: number;
    totalAssignments: number;
    completedCount: number;
    inProgressCount: number;
    notStartedCount: number;
    completionRate: number;
    memberProgress: MemberProgress[];
}

export interface ProductProgressReport {
    productId: number;
    productName: string;
    curriculumTitles: string[];
    licensedSeats: number;
    inProgressCount: number;
    completedCount: number;
    totalEnrolled: number;
    completionRate: number;
    enrollmentRate: number;
}

export interface DepartmentProgressReport {
    departmentId: number;
    departmentName: string;
    memberCount: number;
    totalAssignments: number;
    completedCount: number;
    inProgressCount: number;
    completionRate: number;
}

export interface IndividualProgressReport {
    userId: number;
    email: string;
    fullName: string;
    departmentName: string | null;
    totalAssignments: number;
    completedAssignments: number;
    inProgressAssignments: number;
    notStartedAssignments: number;
    averageProgress: number;
    completionRate: number;
    lastActivityAt: string | null;
}

export interface LearnerProgressDetail {
    userId: number;
    fullName: string;
    departmentName: string | null;
    position: string | null;
    phoneNumber: string | null;
    email: string;
    classId: number;
    className: string;
    classProductId: number | null;
    progressPercent: number;
    lessonQuizAccuracy: number | null;
    checkupQuizAccuracy: number | null;
    preAiqCompleted: boolean;
    postAiqCompleted: boolean;
    aiqGrowthRate: number | null;
    learningStatus: string;
}

export interface LearnerProgressListResponse {
    learners: LearnerProgressDetail[];
    totalCount: number;
    classFilteredCount: number | null;
}

// Learner Progress Detail Modal types
export interface LearnerProgressDetailModalData {
    userId: number
    fullName: string
    className: string
    progressPercent: number
    learningStatus: string
    summary: {
        totalLessons: number
        completedLessons: number
        lessonQuizAccuracy: number | null
        checkupQuizAccuracy: number | null
        lastAccessedAt: string | null
    }
    courses: CourseProgressDetail[]
}

export interface CourseProgressDetail {
    contentId: number
    title: string
    status: string
    progressPercent: number
    modules: ModuleProgressDetail[]
}

export interface ModuleProgressDetail {
    contentId: number
    title: string
    status: string
    progressPercent: number
    completedChildren: number
    totalChildren: number
    lessons: LessonProgressDetail[]
}

export interface LessonProgressDetail {
    contentId: number
    title: string
    status: string
    components: ComponentProgressDetail[]
}

export interface ComponentProgressDetail {
    type: string
    location: string
    status: string
    completionCount: number
    quizScore: number | null
    quizPassed: boolean | null
    videoWatchSeconds: number | null
}

export interface ClassProgressSummary {
    totalClassMembers: number;
    completedCount: number;
    inProgressCount: number;
    notStartedCount: number;
    completionRate: number;
    averageProgressRate: number;
}

export interface CurriculumProgress {
    curriculumId: number;
    curriculumTitle: string;
    totalLessons: number;
    inProgressLearnerCount: number;
    completedLearnerCount: number;
}

export interface DashboardClassDetail {
    classId: number;
    className: string;
    courseCount: number;
    learnerCount: number;
    notStartedCount: number;
    progressRate: number;
    completedCount: number;
    completionRate: number;
    moduleQuizAccuracy: number | null;
    aiqGrowthRate: number | null;
}

export const reportApi = {
    getProgressReport: () =>
        apiClient.get<ApiResponse<ProgressReport>>("/b2b/reports/progress"),
    getClassProgressSummary: () =>
        apiClient.get<ApiResponse<ClassProgressSummary>>("/b2b/reports/progress/by-class-summary"),
    getDashboardClassDetails: () =>
        apiClient.get<ApiResponse<DashboardClassDetail[]>>("/b2b/reports/dashboard/classes"),
    getCurriculumProgressReport: () =>
        apiClient.get<ApiResponse<CurriculumProgress[]>>(
            "/b2b/reports/progress/by-curriculum"
        ),
    getProductProgressReport: () =>
        apiClient.get<ApiResponse<ProductProgressReport[]>>(
            "/b2b/reports/progress/by-product"
        ),
    getDepartmentProgressReport: () =>
        apiClient.get<ApiResponse<DepartmentProgressReport[]>>(
            "/b2b/reports/progress/by-department"
        ),
    getIndividualProgressReport: (search?: string) =>
        apiClient.get<ApiResponse<IndividualProgressReport[]>>(
            `/b2b/reports/progress/by-individual${search ? `?search=${encodeURIComponent(search)}` : ""
            }`
        ),
    getLearnerProgressDetail: (params?: {
        search?: string;
        groupId?: number;
        classId?: number;
        status?: string;
    }) =>
        apiClient.get<ApiResponse<LearnerProgressListResponse>>(
            "/b2b/reports/progress/learners",
            { params }
        ),
    getLearnerProgressDetailModal: (userId: number, classProductId: number) =>
        apiClient.get<ApiResponse<LearnerProgressDetailModalData>>(
            `/b2b/reports/progress/learners/${userId}/detail`,
            { params: { classProductId } }
        ),
};

// ============================================================================
// Learning Center Class Types & API
// ============================================================================

export interface LcClassLicenseInfo {
    licenseId: number;
    productId: number;
    productName: string | null;
    curriculumTitle: string | null;
    allowConfigEdit?: boolean;
}

export interface LcClass {
    id: number;
    organizationId: number;
    name: string;
    description: string | null;
    schedule: Record<string, unknown> | null;
    teacherUserId: number | null;
    teacherName: string | null;
    maxStudents: number;
    currentStudents: number;
    isActive: boolean;
    startDate: string | null;
    endDate: string | null;
    createdAt: string;
    licenses: LcClassLicenseInfo[] | null;  // 연결된 상품(라이선스) 정보
    deadline: string | null;  // 학습 마감일
    allowSelfRegistration: boolean;  // 학습자 자유 등록 허용
    allowAdminAssignment: boolean;   // Admin 할당 허용
}

export interface LcClassRequest {
    name: string;
    description?: string;
    schedule?: Record<string, unknown>;
    teacherUserId?: number;
    maxStudents?: number;
    startDate?: string;
    endDate?: string;
    licenseIds?: number[];  // 연결할 라이선스(상품) ID 목록
    deadline?: string;  // 학습 마감일
    allowSelfRegistration?: boolean;  // 학습자 자유 등록 허용
    allowAdminAssignment?: boolean;   // Admin 할당 허용
}

export interface LcClassMember {
    userId: number;
    fullName: string;
    email: string;
    status: string;
    notes: string | null;
    joinedAt: string | null;
}

export interface LcClassMemberRequest {
    userIds: number[];
}

export interface LcClassMemberUpdateRequest {
    status?: string;
    notes?: string;
}

export interface LcClassStats {
    totalClasses: number;
    activeClasses: number;
    totalStudents: number;
}

export const lcClassApi = {
    getAll: () => apiClient.get<ApiResponse<LcClass[]>>("/b2b/classes"),

    getAllIncludingInactive: () => 
        apiClient.get<ApiResponse<LcClass[]>>("/b2b/classes/all"),

    getById: (id: number) =>
        apiClient.get<ApiResponse<LcClass>>(`/b2b/classes/${id}`),

    create: (data: LcClassRequest) =>
        apiClient.post<ApiResponse<LcClass>>("/b2b/classes", data),

    update: (id: number, data: Partial<LcClassRequest>) =>
        apiClient.put<ApiResponse<LcClass>>(`/b2b/classes/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${id}`),

    activate: (id: number) =>
        apiClient.post<ApiResponse<LcClass>>(`/b2b/classes/${id}/activate`),

    // 멤버(학생) 관리
    getMembers: (classId: number) =>
        apiClient.get<ApiResponse<LcClassMember[]>>(`/b2b/classes/${classId}/members`),

    addMembers: (classId: number, userIds: number[]) =>
        apiClient.post<ApiResponse<LcClassMember[]>>(`/b2b/classes/${classId}/members`, { userIds }),

    removeMembers: (classId: number, userIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${classId}/members`, { data: { userIds } }),

    updateMember: (classId: number, userId: number, data: LcClassMemberUpdateRequest) =>
        apiClient.put<ApiResponse<LcClassMember>>(`/b2b/classes/${classId}/members/${userId}`, data),

    // 상품(라이선스) 관리
    getProducts: (classId: number) =>
        apiClient.get<ApiResponse<number[]>>(`/b2b/classes/${classId}/products`),

    addProducts: (classId: number, licenseIds: number[]) =>
        apiClient.post<ApiResponse<LcClassLicenseInfo[]>>(`/b2b/classes/${classId}/products`, { licenseIds }),

    removeProducts: (classId: number, licenseIds: number[]) =>
        apiClient.delete<ApiResponse<void>>(`/b2b/classes/${classId}/products`, { data: { licenseIds } }),

    // 통계
    getStats: () =>
        apiClient.get<ApiResponse<LcClassStats>>("/b2b/classes/stats"),
};

// ============================================================================
// Admin Notes Types & API
// ============================================================================

export interface AdminNote {
    id: number;
    content: string | null;
    color: string;
    isCompleted: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AdminNoteCreateRequest {
    content?: string;
    color?: string;
}

export interface AdminNoteUpdateRequest {
    content?: string;
    color?: string;
    isCompleted?: boolean;
}

export const adminNoteApi = {
    getAll: () =>
        apiClient.get<ApiResponse<AdminNote[]>>("/v1/b2b/notes"),

    create: (data: AdminNoteCreateRequest) =>
        apiClient.post<ApiResponse<AdminNote>>("/v1/b2b/notes", data),

    update: (id: number, data: AdminNoteUpdateRequest) =>
        apiClient.put<ApiResponse<AdminNote>>(`/v1/b2b/notes/${id}`, data),

    delete: (id: number) =>
        apiClient.delete<ApiResponse<void>>(`/v1/b2b/notes/${id}`),
};
